import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { Box, Chip, IconButton, TablePagination, Tooltip, Typography } from '@mui/material';
import type React from 'react';
import { type Column, StandardTable } from '../../common/UI/StandardTable';
import type { ActivityData, RecentActivity } from './types';

interface RecentEventsTabProps {
  data: ActivityData;
  totalCount: number;
  rowsPerPage: number;
  page: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;
  getUserLabel: (userId?: string) => string;
  formatTimestamp: (timestamp: string) => string;
  getActivityTypeColor: (
    type: string
  ) => 'success' | 'info' | 'warning' | 'secondary' | 'primary' | 'error' | 'default';
  handleViewDetails: (activity: RecentActivity) => void;
}

export const RecentEventsTab: React.FC<RecentEventsTabProps> = ({
  data,
  totalCount,
  rowsPerPage,
  page,
  setPage,
  setRowsPerPage,
  getUserLabel,
  formatTimestamp,
  getActivityTypeColor,
  handleViewDetails,
}) => {
  if (!data.recentActivities) {
    return null;
  }

  const renderActivityDetails = (activity: RecentActivity) => {
    if (activity.activityType === 'login') {
      return (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '1rem' }}>
            <strong>IP:</strong> {activity.clientInfo?.ip || 'N/A'}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '300px',
            }}
          >
            <strong>UA:</strong> {activity.clientInfo?.userAgent || 'N/A'}
          </Typography>
        </Box>
      );
    }

    const payload = activity.data as Record<string, unknown>;

    if (activity.activityType === 'security_event') {
      return (
        <Box>
          {Boolean(payload.event) && (
            <Typography variant="body1" sx={{ fontSize: '1rem' }}>
              <strong>Event:</strong> {String(payload.event)}
            </Typography>
          )}
          {Boolean(payload.tenderId) && (
            <Typography variant="body1" sx={{ fontSize: '1rem' }}>
              <strong>Tender:</strong> {String(payload.tenderId)}
            </Typography>
          )}
          {Boolean(payload.method) && (
            <Typography variant="body1" sx={{ fontSize: '1rem' }}>
              <strong>Method:</strong> {String(payload.method)}
            </Typography>
          )}
          {activity.clientInfo?.ip && (
            <Typography variant="body1" sx={{ fontSize: '1rem', mt: 0.5 }}>
              <strong>IP:</strong> {activity.clientInfo.ip}
            </Typography>
          )}
        </Box>
      );
    }

    const { tenderTitle, fileName, updateType } = payload;
    if (tenderTitle) {
      return (
        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
          <strong>Tender:</strong> {String(tenderTitle)}
        </Typography>
      );
    }
    if (fileName) {
      return (
        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
          <strong>File:</strong> {String(fileName)}
        </Typography>
      );
    }
    if (updateType) {
      return (
        <Typography variant="body1" sx={{ fontSize: '1rem' }}>
          <strong>Action:</strong> {String(updateType)}
        </Typography>
      );
    }

    return (
      <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
        No summary available
      </Typography>
    );
  };

  const columns: Column<RecentActivity>[] = [
    {
      id: 'activity',
      label: 'Activity',
      render: (activity) => (
        <Chip
          label={activity.activityType.replace(/_/g, ' ').toUpperCase()}
          size="small"
          color={getActivityTypeColor(activity.activityType)}
        />
      ),
    },
    {
      id: 'user',
      label: 'User',
      render: (activity) => getUserLabel(activity.userId),
    },
    {
      id: 'role',
      label: 'Role',
      render: (activity) => <Chip label={activity.userRole} size="small" variant="outlined" />,
    },
    {
      id: 'timestamp',
      label: 'Timestamp',
      render: (activity) => formatTimestamp(activity.timestamp),
    },
    {
      id: 'details',
      label: 'Details',
      render: (activity) => renderActivityDetails(activity),
    },
    {
      id: 'action',
      label: 'Action',
      render: (activity) => (
        <Tooltip title="View Full Details">
          <IconButton size="small" onClick={() => handleViewDetails(activity)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ pl: 2 }}>
        Recent Activities
      </Typography>
      <StandardTable
        columns={columns}
        data={data.recentActivities}
        keyExtractor={(activity) => activity.id}
      />
      <TablePagination
        rowsPerPageOptions={[20, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
};
